import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface Greenhouse {
  id: string;
  greenhouseName: string;
  greenhouseCode: string;
  location: string;
  description: string;
  sizeSquareMeters: number;
  structureType: string;
  maxCapacity: number;
  hasIrrigation: boolean;
  hasClimateControl: boolean;
  hasAutomation: boolean;
  commissionedDate: string;
  managerUserId: string;
  managerUserName?: string;
  managerEmail?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateGreenhouseRequest {
  greenhouseName: string;
  greenhouseCode: string;
  location: string;
  description: string;
  sizeSquareMeters: number;
  structureType: string;
  maxCapacity: number;
  hasIrrigation: boolean;
  hasClimateControl: boolean;
  hasAutomation: boolean;
  commissionedDate: string;
  managerUserId: string;
}

export interface UpdateGreenhouseRequest {
  id: string;
  greenhouseName: string;
  location: string;
  description: string;
  sizeSquareMeters: number;
  structureType: string;
  maxCapacity: number;
  hasIrrigation: boolean;
  hasClimateControl: boolean;
  hasAutomation: boolean;
  isActive: boolean;
  commissionedDate: string;
  managerUserId: string;
}

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
  firstName: string;
  otherNames: string;
}

export interface GreenhouseBed {
  id: string;
  bayId: string;
  bayName: string;
  bedNumber: string;
  description?: string;
  sizeSquareMeters?: number;
  plantCapacity?: number;
  currentOccupancy: number;
  isOccupied: boolean;
  isActive: boolean;
}

export interface GreenhouseLayoutStock {
  stockEntryId: string;
  sowingInstructionId?: string;
  instructionNumber?: string;
  cropType: string;
  variety?: string;
  trayType?: string;
  stockUnit: string;
  placementQuantity: number;
  totalQuantity: number;
  fillingReserve: number;
  reservedQuantity: number;
  dispatchedQuantity: number;
  availableQuantity: number;
  isReadyForDispatch: boolean;
  isActive: boolean;
  entryDate: string;
  expectedReadyDate?: string;
}

export interface GreenhouseLayoutBed {
  id: string;
  number: string;
  capacity?: number;
  recordedOccupancy: number;
  calculatedOccupancy: number;
  availableCapacity: number;
  status: "Empty" | "Occupied";
  occupiedSince?: string;
  warnings: string[];
  stock: GreenhouseLayoutStock[];
}

export interface GreenhouseLayoutBay {
  id: string;
  name: string;
  capacity: number;
  occupancy: number;
  availableCapacity: number;
  warningCount: number;
  beds: GreenhouseLayoutBed[];
}

export interface GreenhouseLayoutGreenhouse {
  id: string;
  name: string;
  code: string;
  location?: string;
  capacity: number;
  occupancy: number;
  availableCapacity: number;
  warningCount: number;
  bays: GreenhouseLayoutBay[];
}

export interface GreenhouseLayout {
  asOf: string;
  totalGreenhouses: number;
  totalBeds: number;
  totalCapacity: number;
  calculatedOccupancy: number;
  availableCapacity: number;
  warningCount: number;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  greenhouseOptions: Array<{ id: string; name: string; code: string }>;
  greenhouses: GreenhouseLayoutGreenhouse[];
}

export interface GreenhouseLayoutParams {
  greenhouseId?: string;
  search?: string;
  status?: string;
  pageNumber: number;
  pageSize: number;
}

export const greenhousesApi = {
  getLayout: async (params: GreenhouseLayoutParams): Promise<GreenhouseLayout> => {
    const { data } = await apiClient.get<ApiResponse<GreenhouseLayout>>(
      '/Greenhouses/layout',
      { params }
    );
    return data.data;
  },

  getAllGreenhouses: async (): Promise<Greenhouse[]> => {
    const { data } = await apiClient.get<ApiResponse<Greenhouse[]>>(
      '/Greenhouses'
    );
    return data.data;
  },

  getGreenhouseById: async (id: string): Promise<Greenhouse> => {
    const { data } = await apiClient.get<ApiResponse<Greenhouse>>(
      `/Greenhouses/${id}`
    );
    return data.data;
  },

  createGreenhouse: async (
    payload: CreateGreenhouseRequest
  ): Promise<Greenhouse> => {
    const { data } = await apiClient.post<ApiResponse<Greenhouse>>(
      '/Greenhouses',
      payload
    );
    return data.data;
  },

  updateGreenhouse: async (
    id: string,
    payload: UpdateGreenhouseRequest
  ): Promise<Greenhouse> => {
    const { data } = await apiClient.put<ApiResponse<Greenhouse>>(
      `/Greenhouses/${id}`,
      payload
    );
    return data.data;
  },

  deleteGreenhouse: async (id: string): Promise<void> => {
    await apiClient.delete(`/Greenhouses/${id}`);
  },

  getAvailableBeds: async (greenhouseId: string): Promise<GreenhouseBed[]> => {
    // Endpoint returns a raw array (no ApiResponse envelope), so use the body as-is.
    const { data } = await apiClient.get<GreenhouseBed[]>(
      `/Greenhouses/${greenhouseId}/beds/available`
    );
    return data;
  },

  /** All beds for a greenhouse, including occupied. */
  getGreenhouseBeds: async (greenhouseId: string): Promise<GreenhouseBed[]> => {
    const { data } = await apiClient.get<GreenhouseBed[]>(
      `/Greenhouses/${greenhouseId}/beds`
    );
    return data;
  },

  // User search for manager assignment
  searchUsers: async (searchTerm: string): Promise<UserSearchResult[]> => {
    const { data } = await apiClient.get<ApiResponse<UserSearchResult[]>>(
      `/Users/search?query=${encodeURIComponent(searchTerm)}`
    );
    return data.data;
  },
};
